import { useCallback, useRef, useState } from "react";

// Procedurally synthesized cyberpunk ambient pad using the Web Audio API.
// Avoids external licensed audio files entirely; started lazily on user gesture.
export function useAmbientSound() {
  const [enabled, setEnabled] = useState(false);
  const ctxRef = useRef(null);
  const masterGainRef = useRef(null);
  const nodesRef = useRef([]);

  const buildGraph = useCallback((ctx) => {
    const masterGain = ctx.createGain();
    masterGain.gain.value = 0;
    masterGain.connect(ctx.destination);

    const delay = ctx.createDelay();
    delay.delayTime.value = 0.6;
    const feedback = ctx.createGain();
    feedback.gain.value = 0.25;
    delay.connect(feedback);
    feedback.connect(delay);
    delay.connect(masterGain);

    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 800;
    filter.connect(masterGain);
    filter.connect(delay);

    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.05;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start();

    const freqs = [55, 82.4, 110, 164.8];
    const oscillators = freqs.map((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = i % 2 === 0 ? "sine" : "triangle";
      osc.frequency.value = f;
      const gain = ctx.createGain();
      gain.gain.value = 0.18;
      osc.connect(gain);
      gain.connect(filter);
      osc.start();
      return osc;
    });

    nodesRef.current = [...oscillators, lfo];
    masterGainRef.current = masterGain;
  }, []);

  const toggle = useCallback(() => {
    if (!ctxRef.current) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      buildGraph(ctx);
    }
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();

    setEnabled((prev) => {
      const next = !prev;
      const gainNode = masterGainRef.current;
      const now = ctx.currentTime;
      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.linearRampToValueAtTime(next ? 0.22 : 0, now + 0.8);
      return next;
    });
  }, [buildGraph]);

  return { enabled, toggle };
}
